import React, { useState } from 'react'
import { ImageUpload } from './components/ImageUpload'
import { GenerateButton } from './components/GenerateButton'
import { ResultDisplay } from './components/ResultDisplay'
import { Header } from './components/Header'
import { apiService } from './services/api'
import './App.css'

function App() {
  const [image, setImage] = useState(null)
  const [age, setAge] = useState('Keep Original')
  const [ethnicity, setEthnicity] = useState('Keep Original')
  const [gender, setGender] = useState('Keep Original')
  const [imageCount, setImageCount] = useState('1')
  const [targetPerson, setTargetPerson] = useState('main')
  const [prompt, setPrompt] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)
  const [refinementPrompt, setRefinementPrompt] = useState('')
  const [refinementLoading, setRefinementLoading] = useState(false)
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)
  const [generatedImages, setGeneratedImages] = useState([])

  // Générer le prompt automatiquement quand les valeurs changent
  React.useEffect(() => {
    const generatedPrompt = generateAutoPrompt({ age, ethnicity, gender, targetPerson })
    setPrompt(generatedPrompt)
  }, [age, ethnicity, gender, targetPerson])

  const generateAutoPrompt = (values) => {
    const ageMap = {
      'Keep Original': 'as is',
      'Younger (20s)': 'much younger, in their early 20s, with youthful facial features, smooth skin, and a fresh appearance',
      'Middle-Aged (40s)': 'middle-aged, in their 40s, with some visible aging signs like fine lines and mature facial structure',
      'Older (60s+)': 'significantly older, in their 60s or 70s, with prominent wrinkles, age spots, gray or white hair, and mature facial features'
    };

            const ethnicityMap = {
              'Keep Original': 'as is',
              'Afro American': 'of African American descent with dark skin, African facial features, and textured hair',
              'South Asian': 'of South Asian descent with South Asian facial features, dark hair, and South Asian skin tone',
              'Arabic': 'of Arabic/Middle Eastern descent with Middle Eastern facial features, olive skin, and dark hair',
              'Caucasian': 'of European descent with fair skin, European facial features, and straight or wavy hair',
              'East Asian': 'of East Asian descent with East Asian facial features, straight black hair, and East Asian skin tone',
              'Hispanic': 'of Hispanic/Latin American descent with Latin facial features, olive or tan skin, and dark hair'
            };

    const genderMap = {
      'Keep Original': 'as is',
      'Masculine': 'with more masculine features including stronger jawline, broader shoulders, and masculine facial structure',
      'Feminine': 'with more feminine features including softer jawline, delicate facial structure, and feminine characteristics'
    };

    const age = ageMap[values.age] || 'as is';
    const ethnicity = ethnicityMap[values.ethnicity] || 'as is';
    const gender = genderMap[values.gender] || 'as is';

    let changes = [];
    if (age !== 'as is') changes.push(`- **Age Transformation:** Transform the person to appear ${age}.`);
    if (ethnicity !== 'as is') changes.push(`- **Ethnicity Transformation:** Change the person's ethnicity to ${ethnicity}.`);
    if (gender !== 'as is') changes.push(`- **Gender Transformation:** Modify the person to have ${gender}.`);

    if (changes.length === 0) {
      return 'Please keep the image exactly as it is, without any modifications.';
    }

    const targetText = values.targetPerson === 'main' 
      ? 'the main person in this image' 
      : 'all people in this image';

    return `Please adapt ${targetText} according to the following guidelines:
${changes.join('\n')}

**IMPORTANT CONSTRAINTS:**
- **Preserve Everything Else:** Keep the exact same background, lighting, composition, clothing, poses, and all other visual elements unchanged
- **Only Modify Specified Characteristics:** Change ONLY the age, ethnicity, and/or gender as requested
- **Maintain Natural Proportions:** Ensure the changes look natural and realistic
- **Keep Original Quality:** Preserve the same image quality, resolution, and artistic style

**Creative Direction:**
- **Minimal Changes:** Make only the necessary adjustments to achieve the requested demographic changes
- **Natural Integration:** Ensure the modified characteristics blend seamlessly with the original image
- **Preserve Context:** Keep the same setting, mood, and atmosphere of the original photograph`;
  }

  const handleGenerate = async () => {
    if (!image) {
      setError('Veuillez sélectionner une image')
      return
    }

    setLoading(true)
    setError(null)
    setResult(null)
    setGeneratedImages([])
    setSelectedImageIndex(0)

    try {
      const response = await apiService.generateImage({
        prompt: prompt,
        image: image.data,
        mimeType: image.type,
        imageCount: parseInt(imageCount),
        targetPerson: targetPerson
      })

      setResult(response)
      
      // Gérer la réponse selon le format
      if (response.images && response.images.length > 1) {
        // Nouveau format avec plusieurs images
        setGeneratedImages(response.images)
        setSelectedImageIndex(0)
      } else {
        // Ancien format ou une seule image
        setGeneratedImages([{ id: 0, image: response.image, selected: true }])
        setSelectedImageIndex(0)
      }
    } catch (err) {
      setError(err.message || 'Erreur lors de la génération')
    } finally {
      setLoading(false)
    }
  }

  const handleRefinement = async () => {
    if (!generatedImages.length || !refinementPrompt.trim()) {
      setError('Veuillez entrer une instruction de retouche')
      return
    }

    setRefinementLoading(true)
    setError(null)

    try {
      const selectedImage = generatedImages[selectedImageIndex]
      const response = await apiService.generateImage({
        prompt: refinementPrompt,
        image: selectedImage.image.data,
        mimeType: selectedImage.image.mimeType,
        imageCount: 1,
        targetPerson: targetPerson
      })

      // Mettre à jour seulement l'image sélectionnée
      const updatedImages = [...generatedImages]
      updatedImages[selectedImageIndex] = {
        ...selectedImage,
        image: response.image
      }
      setGeneratedImages(updatedImages)
      setResult(response)
    } catch (err) {
      setError(err.message || 'Erreur lors de la retouche')
    } finally {
      setRefinementLoading(false)
    }
  }

  const handleReset = () => {
    setImage(null)
    setAge('Keep Original')
    setEthnicity('Keep Original')
    setGender('Keep Original')
    setImageCount('1')
    setTargetPerson('main')
    setResult(null)
    setError(null)
    setRefinementPrompt('')
    setGeneratedImages([])
    setSelectedImageIndex(0)
  }

  return (
    <div className="app">
      <Header />
      
      <main className="main-content">
        <div className="control-panel">
          <div className="upload-section">
            <ImageUpload 
              image={image} 
              onImageChange={setImage}
              disabled={loading}
            />
          </div>

          <div className="demographic-inputs">
            <div className="input-group">
              <label htmlFor="age">Age:</label>
              <select
                id="age"
                value={age}
                onChange={(e) => setAge(e.target.value)}
                disabled={loading}
                className="custom-select"
              >
                <option value="Keep Original">Keep Original</option>
                <option value="Younger (20s)">Younger (20s)</option>
                <option value="Middle-Aged (40s)">Middle-Aged (40s)</option>
                <option value="Older (60s+)">Older (60s+)</option>
              </select>
            </div>

            <div className="input-group">
              <label htmlFor="ethnicity">Ethnicity:</label>
              <select
                id="ethnicity"
                value={ethnicity}
                onChange={(e) => setEthnicity(e.target.value)}
                disabled={loading}
                className="custom-select"
              >
                <option value="Keep Original">Keep Original</option>
                <option value="Afro American">Afro American</option>
                <option value="South Asian">South Asian</option>
                <option value="Arabic">Arabic</option>
                <option value="Caucasian">Caucasian</option>
                <option value="East Asian">East Asian</option>
                <option value="Hispanic">Hispanic</option>
              </select>
            </div>

            <div className="input-group">
              <label htmlFor="gender">Gender:</label>
              <select
                id="gender"
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                disabled={loading}
                className="custom-select"
              >
                <option value="Keep Original">Keep Original</option>
                <option value="Masculine">Masculine</option>
                <option value="Feminine">Feminine</option>
              </select>
            </div>

            <div className="input-group">
              <label htmlFor="imageCount">Number of Images:</label>
              <select
                id="imageCount"
                value={imageCount}
                onChange={(e) => setImageCount(e.target.value)}
                disabled={loading}
                className="custom-select"
              >
                <option value="1">1 Image</option>
                <option value="3">3 Images</option>
                <option value="5">5 Images</option>
              </select>
            </div>

            <div className="input-group">
              <label htmlFor="targetPerson">Target:</label>
              <select
                id="targetPerson"
                value={targetPerson}
                onChange={(e) => setTargetPerson(e.target.value)}
                disabled={loading}
                className="custom-select"
              >
                <option value="main">Main Person Only</option>
                <option value="all">All People</option>
              </select>
            </div>
          </div>

          <div className="prompt-section">
            <label htmlFor="prompt">Generated Prompt:</label>
            <p className="prompt-description">
              This is the exact prompt that will be sent to the AI for market adaptation.
            </p>
            <textarea
              id="prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              disabled={loading}
              rows="8"
              placeholder="Your AI prompt will appear here..."
            />
          </div>

          <div className="action-buttons">
            <button 
              className="reset-button"
              onClick={handleReset}
              disabled={loading}
            >
              Reset
            </button>
            
            <button 
              className="adapt-button"
              onClick={handleGenerate}
              disabled={!image || loading}
            >
              {loading ? 'Adapting...' : 'Adapt Image'}
            </button>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <p>{error}</p>
          </div>
        )}

        <div className="image-panels">
          <div className="image-panel">
            <h3>Generated Images</h3>
            {loading ? (
              <div className="loading-container">
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  <p>Generating {imageCount} image{imageCount > 1 ? 's' : ''}...</p>
                </div>
              </div>
            ) : generatedImages.length > 0 ? (
              <div className="gallery-container">
                {generatedImages.length > 1 && (
                  <div className="gallery-thumbnails">
                    {generatedImages.map((img, index) => (
                      <div 
                        key={img.id}
                        className={`thumbnail ${selectedImageIndex === index ? 'selected' : ''}`}
                        onClick={() => setSelectedImageIndex(index)}
                      >
                        <img 
                          src={`data:${img.image.mimeType};base64,${img.image.data}`} 
                          alt={`Generated ${index + 1}`}
                        />
                        <span className="thumbnail-number">{index + 1}</span>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="main-image-container">
                  <ResultDisplay result={generatedImages[selectedImageIndex]} />
                  <button 
                    className="fullscreen-button"
                    onClick={() => {
                      const selectedImg = generatedImages[selectedImageIndex]
                      const imageUrl = `data:${selectedImg.image.mimeType};base64,${selectedImg.image.data}`;
                      const newWindow = window.open('', '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
                      newWindow.document.write(`
                        <!DOCTYPE html>
                        <html>
                          <head>
                            <title>Generated Image - Fullscreen</title>
                            <style>
                              body { 
                                margin: 0; 
                                padding: 20px; 
                                background: #f8fafc; 
                                display: flex; 
                                justify-content: center; 
                                align-items: center; 
                                min-height: 100vh;
                                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                              }
                              .image-container {
                                max-width: 90vw;
                                max-height: 90vh;
                                text-align: center;
                              }
                              img { 
                                max-width: 100%; 
                                max-height: 100%; 
                                object-fit: contain; 
                                border-radius: 12px; 
                                box-shadow: 0 8px 32px rgba(0,0,0,0.15);
                              }
                              .download-btn {
                                margin-top: 20px;
                                padding: 10px 20px;
                                background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
                                color: white;
                                border: none;
                                border-radius: 8px;
                                cursor: pointer;
                                font-size: 14px;
                              }
                            </style>
                          </head>
                          <body>
                            <div class="image-container">
                              <img src="${imageUrl}" alt="Generated Image" />
                              <br>
                              <button class="download-btn" onclick="window.close()">Close</button>
                            </div>
                          </body>
                        </html>
                      `);
                    }}
                    title="View in fullscreen"
                  >
                    🔍 Fullscreen
                  </button>
                </div>
              </div>
            ) : (
              <div className="panel-placeholder">
                <p>Generated images will appear here</p>
              </div>
            )}
          </div>

          {generatedImages.length > 0 && (
            <div className="refinement-section">
              <h3>Image Refinement</h3>
              <p className="refinement-description">
                Make fine adjustments to the selected image (Image {selectedImageIndex + 1}). Describe what you want to change:
              </p>
              <div className="refinement-controls">
                <textarea
                  value={refinementPrompt}
                  onChange={(e) => setRefinementPrompt(e.target.value)}
                  placeholder="e.g., 'Make the smile more natural', 'Adjust the lighting', 'Change the background color'..."
                  rows="3"
                  className="refinement-textarea"
                  disabled={refinementLoading}
                />
                <button
                  onClick={handleRefinement}
                  disabled={refinementLoading || !refinementPrompt.trim()}
                  className="refinement-button"
                >
                  {refinementLoading ? (
                    <>
                      <div className="spinner-small"></div>
                      Refining...
                    </>
                  ) : (
                    'Apply Refinement'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}

export default App